import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface OrganizationalUnitModel {
  id?: string;
  name: string;
  code: string;
  selected?: boolean;
  clientID?: string;
  searchKeyword?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationalUnitDataService {
  constructor(private snackBar: MatSnackBar) {}

  private OU_DATA = new BehaviorSubject<OrganizationalUnitModel[]>([]);
  private dataStore: { units: OrganizationalUnitModel[] } = { units: [] };
  readonly units = this.OU_DATA.asObservable();
  private nextToken: string | undefined;

  resetData() {
    this.dataStore.units = [];
    this.OU_DATA.next([]);
  }

  async listByClientID(
    clientID: string,
    filter?: any,
    limit?: number,
    nextToken?: string
  ): Promise<{ items: OrganizationalUnitModel[]; nextToken: string | null }> {
    const statement = `query GetOrganizationalUnitsByClientID($clientID: ID!, $filter: ModelOrganizationalUnitFilterInput, $limit: Int, $nextToken: String) {
      getOrganizationalUnitsByClientID(clientID: $clientID, filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {
          id
          name
          code
          clientID
          createdAt
          updatedAt
        }
        nextToken
      }
    }`;
    const gqlAPIServiceArguments: any = {
      clientID,
      limit: limit || 50,
    };
    if (filter) gqlAPIServiceArguments.filter = filter;
    if (nextToken) gqlAPIServiceArguments.nextToken = nextToken;

    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return response.data.getOrganizationalUnitsByClientID;
  }

  async listAllByClientID(clientID: string): Promise<OrganizationalUnitModel[]> {
    let allItems: OrganizationalUnitModel[] = [];
    let token: string | null = null;
    do {
      const result: any = await this.listByClientID(clientID, undefined, 10000, token || undefined);
      allItems = allItems.concat(result.items);
      token = result.nextToken;
    } while (token);
    return allItems;
  }

  async createOrganizationalUnit(input: OrganizationalUnitModel): Promise<OrganizationalUnitModel> {
    const statement = `mutation CreateOrganizationalUnit($input: CreateOrganizationalUnitInput!) {
      createOrganizationalUnit(input: $input) {
        id
        name
        code
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.createOrganizationalUnit;
  }

  async updateOrganizationalUnit(input: Partial<OrganizationalUnitModel> & { id: string }): Promise<OrganizationalUnitModel> {
    const statement = `mutation UpdateOrganizationalUnit($input: UpdateOrganizationalUnitInput!) {
      updateOrganizationalUnit(input: $input) {
        id
        name
        code
        clientID
        createdAt
        updatedAt
      }
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.updateOrganizationalUnit;
  }

  async deleteOrganizationalUnit(id: string): Promise<void> {
    const statement = `mutation DeleteOrganizationalUnit($input: DeleteOrganizationalUnitInput!) {
      deleteOrganizationalUnit(input: $input) {
        id
      }
    }`;
    await API.graphql(graphqlOperation(statement, { input: { id } }));
  }

  async initializeData(clientID: string): Promise<void> {
    const data = await this.listByClientID(clientID, undefined, 50)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.units = data.items;
    this.OU_DATA.next([...this.dataStore.units]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  async filterUnits(clientID: string, keyword: string): Promise<void> {
    this.dataStore.units = [];
    let token: string | null = null;
    do {
      const data: any = await this.listByClientID(
        clientID,
        {
          or: [
            { code: { contains: keyword } },
            { name: { contains: keyword } },
            { searchKeyword: { contains: keyword } },
          ],
        },
        100000,
        token || undefined
      ).catch((err) => { console.log(err); return null; });
      if (data) {
        this.dataStore.units = [...this.dataStore.units, ...data.items];
        this.OU_DATA.next([...this.dataStore.units]);
        token = data.nextToken;
      } else {
        token = null;
      }
    } while (token);
  }

  async fetchAdditional(clientID: string): Promise<void> {
    if (!this.nextToken) return;
    const data = await this.listByClientID(clientID, undefined, 150, this.nextToken)
      .catch((err) => { console.log(err); return { items: [], nextToken: null }; });
    this.dataStore.units = this.dataStore.units.concat(data.items);
    this.OU_DATA.next([...this.dataStore.units]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  async addUnit(unit: OrganizationalUnitModel): Promise<void> {
    const user = await Auth.currentAuthenticatedUser();
    const input: OrganizationalUnitModel = {
      clientID: user?.attributes.sub,
      name: unit.name,
      code: unit.code,
    };
    const created = await this.createOrganizationalUnit(input);
    this.dataStore.units.unshift(created);
    this.OU_DATA.next([...this.dataStore.units]);
  }

  async editUnit(unitData: OrganizationalUnitModel): Promise<void> {
    const found = this.dataStore.units.find((u) => u.id === unitData.id);
    if (found) {
      const updated = await this.updateOrganizationalUnit({
        id: unitData.id!,
        name: unitData.name,
        code: unitData.code,
      });
      Object.assign(found, updated);
      this.OU_DATA.next([...this.dataStore.units]);
      this.snackBar.open('Changes successfully saved', '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }

  async removeUnit(item: OrganizationalUnitModel): Promise<void> {
    const found = this.dataStore.units.find((u) => u.id === item.id);
    if (found) {
      this.dataStore.units = this.dataStore.units.filter((u) => u.id !== item.id);
      this.OU_DATA.next([...this.dataStore.units]);
      await this.deleteOrganizationalUnit(item.id!).catch((err) => console.log(err));
      this.snackBar.open(`The "${item.name}" unit has been deleted`, '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }
}
